import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Settings as SettingsIcon, User, MessageSquare, Save, Database, RefreshCw, CheckCircle, XCircle, Workflow, Bot, Zap, Play, CreditCard, Code, Copy, Check, Mail, Megaphone, Ticket, Cloud } from "lucide-react";
import { generateEmbedCodes } from "../components/ChatbotWidget";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [testingStores, setTestingStores] = useState(false);
  const [testingRestaurants, setTestingRestaurants] = useState(false);
  const [testingAi, setTestingAi] = useState(false);
  const [testingS3, setTestingS3] = useState(false);
  const [s3Status, setS3Status] = useState(null);
  const [storeApiStatus, setStoreApiStatus] = useState(null);
  const [restaurantApiStatus, setRestaurantApiStatus] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);
  
  const [whatsappConfig, setWhatsappConfig] = useState({
    phone_id: "",
    access_token: "",
    verify_token: "mall_assistant_verify_token",
    webhook_url: window.location.origin + "/api/whatsapp/webhook"
  });

  const [mallConfig, setMallConfig] = useState({
    name: "City Mall",
    address: "123 Main Street, City",
    phone: "+1 234 567 8900",
    email: "info@citymall.com",
    opening_hours_weekday: "10:00 AM - 10:00 PM",
    opening_hours_weekend: "10:00 AM - 11:00 PM"
  });

  const [tableBookingConfig, setTableBookingConfig] = useState({
    mall_email: "",
    mall_whatsapp: "",
    notify_email: true,
    notify_whatsapp: true,
  });

  const [externalApiConfig, setExternalApiConfig] = useState({
    stores_api_url: "",
    stores_api_key: "",
    stores_api_method: "GET",
    restaurants_api_url: "",
    restaurants_api_key: "",
    restaurants_api_method: "GET"
  });

  const [n8nConfig, setN8nConfig] = useState({
    booking_webhook: "",
    whatsapp_webhook: "",
    daily_report_webhook: "",
    enable_webhooks: true
  });

  const [aiConfig, setAiConfig] = useState({
    enable_ai: false,
    provider: "openai",
    openai_api_key: "",
    api_key: "",
    base_url: "",
    model: "gpt-4o",
    has_api_key: false,
    recommended_models: {},
    default_base_urls: {},
    providers: ["openai", "groq", "openrouter", "together", "ollama", "custom"],
    max_output_tokens: 800,
    rate_limit_per_minute: 10,
    rate_limit_per_day: 100,
  });
  const [syncingAiModels, setSyncingAiModels] = useState(false);

  const [westgateConfig, setWestgateConfig] = useState({
    base_url: "https://cms.westgate.co.ke",
    email: "",
    password: "",
    has_credentials: false
  });

  const [testingWestgate, setTestingWestgate] = useState(false);
  const [westgateStatus, setWestgateStatus] = useState(null);
  const [syncingStores, setSyncingStores] = useState(false);
  const [syncingRestaurants, setSyncingRestaurants] = useState(false);
  const [syncingMovies, setSyncingMovies] = useState(false);
  const [autoSyncStatus, setAutoSyncStatus] = useState(null);
  const [runningAutoSync, setRunningAutoSync] = useState(false);

  const [mpesaConfig, setMpesaConfig] = useState({
    consumer_key: "",
    consumer_secret: "",
    shortcode: "174379",
    passkey: "",
    callback_url: "",
    environment: "sandbox"
  });
  const [copiedCode, setCopiedCode] = useState(null);
  
  // SMS API Config
  const [smsConfig, setSmsConfig] = useState({
    sms_url: "https://apis.onfonmedia.co.ke/v1/sms/SendBulkSMS",
    api_username: "",
    api_password: "",
    api_key: "",
    client_id: "",
    access_key: "",
    sender_id: "WESTGATE",
    is_configured: false
  });
  const [testingSms, setTestingSms] = useState(false);
  const [testPhone, setTestPhone] = useState("");

  // Email Config
  const [emailConfig, setEmailConfig] = useState({
    provider: "generic",
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    from_email: "",
    from_name: "Westgate Mall",
    use_tls: true,
    use_ssl: false,
    is_configured: false
  });
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  const [mailchimpConfig, setMailchimpConfig] = useState({
    enabled: false,
    api_key: "",
    server_prefix: "",
    audience_id: "",
    is_configured: false
  });
  const [testingMailchimp, setTestingMailchimp] = useState(false);
  const [syncingMailchimp, setSyncingMailchimp] = useState(false);
  const [mailchimpStatus, setMailchimpStatus] = useState(null);

  const [s3Config, setS3Config] = useState({
    enabled: false,
    access_key_id: "",
    secret_access_key: "",
    region: "",
    bucket: "",
    public_base_url: "",
    prefix: "assets",
    is_configured: false,
    storage: "host-disk"
  });

  const [ticketDelivery, setTicketDelivery] = useState({
    email_enabled: true,
    whatsapp_enabled: true,
    telegram_enabled: false,
    telegram_bot_token: "",
    telegram_chat_id: "",
    seller_name: "Westgate Cinema",
    seller_address: "Westgate Shopping Mall, Mwanzi Road, Nairobi",
    kra_pin: "",
    cu_serial: "",
    vat_rate_percent: 16,
    whatsapp_ready: false
  });

  useEffect(() => {
    fetchApiConfig();
    fetchN8nConfig();
    fetchAiConfig();
    fetchWestgateConfig();
    fetchAutoSyncStatus();
    fetchMpesaConfig();
    fetchSmsConfig();
    fetchEmailConfig();
    fetchMailchimpConfig();
    fetchTicketDelivery();
    fetchWhatsAppConfig();
    fetchS3Config();
    fetchTableBookingConfig();
  }, []);

  const fetchAutoSyncStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/auto-sync`, {
        headers: getAuthHeader(),
        withCredentials: true,
      });
      setAutoSyncStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch auto-sync status:", error);
    }
  };

  const runAutoSyncNow = async () => {
    setRunningAutoSync(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/settings/auto-sync/run`,
        {},
        { headers: getAuthHeader(), withCredentials: true }
      );
      setAutoSyncStatus(response.data);
      toast.success("Auto-sync completed");
      fetchAutoSyncStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Auto-sync failed");
      fetchAutoSyncStatus();
    } finally {
      setRunningAutoSync(false);
    }
  };

  const fetchTableBookingConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/table-booking`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setTableBookingConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Failed to fetch table booking config:", error);
    }
  };

  const fetchApiConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/external-api`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setExternalApiConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Failed to fetch API config:", error);
    }
  };

  const fetchN8nConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/n8n`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setN8nConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Failed to fetch n8n config:", error);
    }
  };

  const fetchAiConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/ai`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setAiConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Failed to fetch AI config:", error);
    }
  };

  const fetchWestgateConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/westgate`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setWestgateConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Failed to fetch Westgate config:", error);
    }
  };

  const fetchWhatsAppConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/whatsapp`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setWhatsappConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Failed to fetch WhatsApp config:", error);
    }
  };

  const handleSaveWhatsApp = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/settings/whatsapp`, {
        phone_id: whatsappConfig.phone_id,
        access_token: whatsappConfig.access_token,
        verify_token: whatsappConfig.verify_token
      }, {
        headers: getAuthHeader()
      });
      toast.success("WhatsApp configuration saved to host .env");
      fetchWhatsAppConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save WhatsApp configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMall = () => {
    setLoading(true);
    setTimeout(() => {
      toast.success("Mall settings saved");
      setLoading(false);
    }, 500);
  };

  const handleSaveTableBooking = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/settings/table-booking`, tableBookingConfig, {
        headers: getAuthHeader()
      });
      toast.success("Table booking mall notifications saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save table booking settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveN8n = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/settings/n8n`, n8nConfig, {
        headers: getAuthHeader()
      });
      toast.success("n8n configuration saved");
    } catch (error) {
      toast.error("Failed to save n8n configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAi = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/settings/ai`, {
        enable_ai: aiConfig.enable_ai,
        provider: aiConfig.provider,
        openai_api_key: aiConfig.openai_api_key,
        api_key: aiConfig.api_key,
        base_url: aiConfig.base_url,
        model: aiConfig.model,
        max_output_tokens: Number(aiConfig.max_output_tokens),
        rate_limit_per_minute: Number(aiConfig.rate_limit_per_minute),
        rate_limit_per_day: Number(aiConfig.rate_limit_per_day),
      }, {
        headers: getAuthHeader()
      });
      toast.success("AI configuration saved");
      fetchAiConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save AI configuration");
    } finally {
      setLoading(false);
    }
  };

  const syncAiModels = async () => {
    setSyncingAiModels(true);
    try {
      const response = await axios.get(`${API_URL}/api/settings/ai`, {
        headers: getAuthHeader()
      });
      const next = response.data || {};
      const provider = aiConfig.provider || next.provider || "openai";
      const models = next.recommended_models?.[provider] || [];
      const selectedModel = models.includes(aiConfig.model)
        ? aiConfig.model
        : (models[0] || aiConfig.model || next.model);
      setAiConfig((prev) => ({
        ...prev,
        ...next,
        provider,
        model: selectedModel,
        recommended_models: next.recommended_models || {},
      }));
      toast.success("Recommended models synced from curated catalog");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync AI models");
    } finally {
      setSyncingAiModels(false);
    }
  };

  const testAiConnection = async () => {
    setTestingAi(true);
    setAiStatus(null);
    try {
      const response = await axios.post(`${API_URL}/api/settings/test-ai`, {}, {
        headers: getAuthHeader()
      });
      setAiStatus({ success: true, message: response.data.message });
      toast.success("AI connection successful");
    } catch (error) {
      setAiStatus({ success: false, message: error.response?.data?.detail || "Connection failed" });
      toast.error("AI connection failed");
    } finally {
      setTestingAi(false);
    }
  };

  const handleSaveWestgate = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/settings/westgate`, westgateConfig, {
        headers: getAuthHeader()
      });
      toast.success("Westgate API configuration saved");
      fetchWestgateConfig();
    } catch (error) {
      toast.error("Failed to save Westgate configuration");
    } finally {
      setLoading(false);
    }
  };

  const testWestgateConnection = async () => {
    setTestingWestgate(true);
    setWestgateStatus(null);
    try {
      const response = await axios.post(`${API_URL}/api/settings/westgate/test`, {}, {
        headers: getAuthHeader()
      });
      setWestgateStatus({ success: true, message: response.data.message });
      toast.success("Westgate API connection successful");
    } catch (error) {
      setWestgateStatus({ success: false, message: error.response?.data?.detail || "Connection failed" });
      toast.error("Westgate API connection failed");
    } finally {
      setTestingWestgate(false);
    }
  };

  const syncWestgateStores = async () => {
    setSyncingStores(true);
    try {
      const response = await axios.post(`${API_URL}/api/westgate/sync/stores`, {}, {
        headers: getAuthHeader()
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync stores");
    } finally {
      setSyncingStores(false);
    }
  };

  const syncWestgateRestaurants = async () => {
    setSyncingRestaurants(true);
    try {
      const response = await axios.post(`${API_URL}/api/westgate/sync/restaurants`, {}, {
        headers: getAuthHeader()
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync restaurants");
    } finally {
      setSyncingRestaurants(false);
    }
  };

  const syncWestgateMovies = async () => {
    setSyncingMovies(true);
    try {
      const response = await axios.post(`${API_URL}/api/westgate/sync/movies`, {}, {
        headers: getAuthHeader()
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync movies");
    } finally {
      setSyncingMovies(false);
    }
  };

  const triggerDailyReport = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/settings/trigger-daily-report`, {}, {
        headers: getAuthHeader()
      });
      toast.success("Daily report webhook triggered");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to trigger webhook");
    }
  };

  const fetchS3Config = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/s3`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setS3Config(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Failed to fetch S3 config:", error);
    }
  };

  const handleSaveS3 = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/settings/s3`, s3Config, {
        headers: getAuthHeader()
      });
      toast.success("S3 configuration saved to host .env");
      fetchS3Config();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save S3 configuration");
    } finally {
      setLoading(false);
    }
  };

  const testS3Connection = async () => {
    setTestingS3(true);
    setS3Status(null);
    try {
      const response = await axios.post(`${API_URL}/api/settings/s3/test`, {}, {
        headers: getAuthHeader()
      });
      setS3Status({ success: true, message: response.data.message, example: response.data.public_url_example });
      toast.success("S3 connection successful");
    } catch (error) {
      setS3Status({ success: false, message: error.response?.data?.detail || "Connection failed" });
      toast.error("S3 connection failed");
    } finally {
      setTestingS3(false);
    }
  };

  const fetchMpesaConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/mpesa`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setMpesaConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Failed to fetch M-Pesa config:", error);
    }
  };

  const handleSaveMpesa = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/settings/mpesa`, mpesaConfig, {
        headers: getAuthHeader()
      });
      toast.success("M-Pesa configuration saved");
      fetchMpesaConfig();
    } catch (error) {
      toast.error("Failed to save M-Pesa configuration");
    } finally {
      setLoading(false);
    }
  };

  const fetchSmsConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/sms`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setSmsConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Failed to fetch SMS config:", error);
    }
  };

  const handleSaveSms = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/settings/sms`, smsConfig, {
        headers: getAuthHeader()
      });
      toast.success("SMS API configuration saved");
      fetchSmsConfig();
    } catch (error) {
      toast.error("Failed to save SMS configuration");
    } finally {
      setLoading(false);
    }
  };

  const testSmsApi = async () => {
    if (!testPhone) {
      toast.error("Please enter a phone number to test");
      return;
    }
    setTestingSms(true);
    try {
      const response = await axios.post(`${API_URL}/api/settings/sms/test`, {
        phone: testPhone
      }, {
        headers: getAuthHeader()
      });
      if (response.data.success) {
        toast.success("Test SMS sent successfully!");
      } else {
        toast.error(response.data.message || "Failed to send test SMS");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test SMS");
    } finally {
      setTestingSms(false);
    }
  };

  const fetchEmailConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/email`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setEmailConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Failed to fetch Email config:", error);
    }
  };

  const handleSaveEmail = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/settings/email`, emailConfig, {
        headers: getAuthHeader()
      });
      toast.success("Email configuration saved");
      fetchEmailConfig();
    } catch (error) {
      toast.error("Failed to save Email configuration");
    } finally {
      setLoading(false);
    }
  };

  const fetchMailchimpConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/mailchimp`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setMailchimpConfig({
          enabled: Boolean(response.data.enabled),
          api_key: response.data.api_key || "",
          server_prefix: response.data.server_prefix || "",
          audience_id: response.data.audience_id || "",
          is_configured: Boolean(response.data.is_configured)
        });
      }
    } catch (error) {
      console.error("Failed to fetch Mailchimp config:", error);
    }
  };

  const handleSaveMailchimp = async () => {
    setLoading(true);
    setMailchimpStatus(null);
    try {
      await axios.post(`${API_URL}/api/settings/mailchimp`, {
        enabled: mailchimpConfig.enabled,
        api_key: mailchimpConfig.api_key,
        server_prefix: mailchimpConfig.server_prefix,
        audience_id: mailchimpConfig.audience_id
      }, {
        headers: getAuthHeader()
      });
      toast.success("Mailchimp configuration saved");
      fetchMailchimpConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save Mailchimp configuration");
    } finally {
      setLoading(false);
    }
  };

  const testMailchimpApi = async () => {
    setTestingMailchimp(true);
    setMailchimpStatus(null);
    try {
      const response = await axios.post(`${API_URL}/api/settings/mailchimp/test`, {}, {
        headers: getAuthHeader()
      });
      setMailchimpStatus({ success: true, message: response.data.message });
      toast.success(response.data.message);
    } catch (error) {
      const detail = error.response?.data?.detail || "Mailchimp connection failed";
      setMailchimpStatus({ success: false, message: detail });
      toast.error(detail);
    } finally {
      setTestingMailchimp(false);
    }
  };

  const syncMailchimpVisitors = async () => {
    setSyncingMailchimp(true);
    try {
      const response = await axios.post(`${API_URL}/api/settings/mailchimp/sync-visitors`, {}, {
        headers: getAuthHeader()
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync visitors to Mailchimp");
    } finally {
      setSyncingMailchimp(false);
    }
  };

  const fetchTicketDelivery = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/ticket-delivery`, {
        headers: getAuthHeader()
      });
      if (response.data) {
        setTicketDelivery({
          email_enabled: Boolean(response.data.email_enabled),
          whatsapp_enabled: Boolean(response.data.whatsapp_enabled),
          telegram_enabled: Boolean(response.data.telegram_enabled),
          telegram_bot_token: response.data.telegram_bot_token || "",
          telegram_chat_id: response.data.telegram_chat_id || "",
          seller_name: response.data.seller_name || "Westgate Cinema",
          seller_address: response.data.seller_address || "",
          kra_pin: response.data.kra_pin || "",
          cu_serial: response.data.cu_serial || "",
          vat_rate_percent: Number(response.data.vat_rate_percent),
          whatsapp_ready: Boolean(response.data.whatsapp_ready)
        });
      }
    } catch (error) {
      console.error("Failed to fetch ticket delivery config:", error);
    }
  };

  const handleSaveTicketDelivery = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/settings/ticket-delivery`, {
        email_enabled: ticketDelivery.email_enabled,
        whatsapp_enabled: ticketDelivery.whatsapp_enabled,
        telegram_enabled: ticketDelivery.telegram_enabled,
        telegram_bot_token: ticketDelivery.telegram_bot_token,
        telegram_chat_id: ticketDelivery.telegram_chat_id,
        seller_name: ticketDelivery.seller_name,
        seller_address: ticketDelivery.seller_address,
        kra_pin: ticketDelivery.kra_pin,
        cu_serial: ticketDelivery.cu_serial,
        vat_rate_percent: Number(ticketDelivery.vat_rate_percent)
      }, {
        headers: getAuthHeader()
      });
      toast.success("Ticket delivery settings saved");
      fetchTicketDelivery();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save ticket delivery settings");
    } finally {
      setLoading(false);
    }
  };

  const testEmailApi = async () => {
    if (!testEmail) {
      toast.error("Please enter an email address to test");
      return;
    }
    setTestingEmail(true);
    try {
      const response = await axios.post(`${API_URL}/api/settings/email/test`, {
        email: testEmail
      }, {
        headers: getAuthHeader()
      });
      if (response.data.success) {
        toast.success("Test email sent successfully!");
      } else {
        toast.error(response.data.message || "Failed to send test email");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setTestingEmail(false);
    }
  };

  const copyToClipboard = (code, type) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(type);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleSaveExternalApi = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/settings/external-api`, externalApiConfig, {
        headers: getAuthHeader()
      });
      toast.success("External API configuration saved");
    } catch (error) {
      toast.error("Failed to save API configuration");
    } finally {
      setLoading(false);
    }
  };

  const testStoresApi = async () => {
    if (!externalApiConfig.stores_api_url) {
      toast.error("Please enter Stores API URL first");
      return;
    }
    setTestingStores(true);
    setStoreApiStatus(null);
    try {
      const response = await axios.post(`${API_URL}/api/settings/test-external-api`, {
        url: externalApiConfig.stores_api_url,
        api_key: externalApiConfig.stores_api_key,
        method: externalApiConfig.stores_api_method
      }, {
        headers: getAuthHeader()
      });
      setStoreApiStatus({ success: true, message: `Connected! Found ${response.data.count || 0} records` });
      toast.success("Stores API connection successful");
    } catch (error) {
      setStoreApiStatus({ success: false, message: error.response?.data?.detail || "Connection failed" });
      toast.error("Stores API connection failed");
    } finally {
      setTestingStores(false);
    }
  };

  const testRestaurantsApi = async () => {
    if (!externalApiConfig.restaurants_api_url) {
      toast.error("Please enter Restaurants API URL first");
      return;
    }
    setTestingRestaurants(true);
    setRestaurantApiStatus(null);
    try {
      const response = await axios.post(`${API_URL}/api/settings/test-external-api`, {
        url: externalApiConfig.restaurants_api_url,
        api_key: externalApiConfig.restaurants_api_key,
        method: externalApiConfig.restaurants_api_method
      }, {
        headers: getAuthHeader()
      });
      setRestaurantApiStatus({ success: true, message: `Connected! Found ${response.data.count || 0} records` });
      toast.success("Restaurants API connection successful");
    } catch (error) {
      setRestaurantApiStatus({ success: false, message: error.response?.data?.detail || "Connection failed" });
      toast.error("Restaurants API connection failed");
    } finally {
      setTestingRestaurants(false);
    }
  };

  const syncStores = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/settings/sync-stores`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`Synced ${response.data.synced_count} stores from external API`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync stores");
    } finally {
      setLoading(false);
    }
  };

  const syncRestaurants = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/settings/sync-restaurants`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`Synced ${response.data.synced_count} restaurants from external API`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync restaurants");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">Configure system settings and integrations</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="rounded-none bg-muted p-1 flex-wrap h-auto">
          <TabsTrigger value="profile" className="rounded-none data-[state=active]:bg-white" data-testid="tab-profile">
            <User className="h-4 w-4 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="rounded-none data-[state=active]:bg-white" data-testid="tab-whatsapp">
            <MessageSquare className="h-4 w-4 mr-2" />
            WhatsApp
          </TabsTrigger>
          <TabsTrigger value="mall" className="rounded-none data-[state=active]:bg-white" data-testid="tab-mall">
            <SettingsIcon className="h-4 w-4 mr-2" />
            Mall Info
          </TabsTrigger>
          <TabsTrigger value="external-api" className="rounded-none data-[state=active]:bg-white" data-testid="tab-external-api">
            <Database className="h-4 w-4 mr-2" />
            External APIs
          </TabsTrigger>
          <TabsTrigger value="n8n" className="rounded-none data-[state=active]:bg-white" data-testid="tab-n8n">
            <Workflow className="h-4 w-4 mr-2" />
            n8n
          </TabsTrigger>
          <TabsTrigger value="ai" className="rounded-none data-[state=active]:bg-white" data-testid="tab-ai">
            <Bot className="h-4 w-4 mr-2" />
            AI Models
          </TabsTrigger>
          <TabsTrigger value="westgate" className="rounded-none data-[state=active]:bg-white" data-testid="tab-westgate">
            <Database className="h-4 w-4 mr-2" />
            Westgate API
          </TabsTrigger>
          <TabsTrigger value="mpesa" className="rounded-none data-[state=active]:bg-white" data-testid="tab-mpesa">
            <CreditCard className="h-4 w-4 mr-2" />
            M-Pesa
          </TabsTrigger>
          <TabsTrigger value="sms" className="rounded-none data-[state=active]:bg-white" data-testid="tab-sms">
            <MessageSquare className="h-4 w-4 mr-2" />
            SMS API
          </TabsTrigger>
          <TabsTrigger value="email" className="rounded-none data-[state=active]:bg-white" data-testid="tab-email">
            <Mail className="h-4 w-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="mailchimp" className="rounded-none data-[state=active]:bg-white" data-testid="tab-mailchimp">
            <Megaphone className="h-4 w-4 mr-2" />
            Mailchimp
          </TabsTrigger>
          <TabsTrigger value="tickets" className="rounded-none data-[state=active]:bg-white" data-testid="tab-tickets">
            <Ticket className="h-4 w-4 mr-2" />
            Tickets
          </TabsTrigger>
          <TabsTrigger value="s3" className="rounded-none data-[state=active]:bg-white" data-testid="tab-s3">
            <Cloud className="h-4 w-4 mr-2" />
            AWS S3
          </TabsTrigger>
          <TabsTrigger value="embed" className="rounded-none data-[state=active]:bg-white" data-testid="tab-embed">
            <Code className="h-4 w-4 mr-2" />
            Embed Widget
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="border-border rounded-none">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Profile Information</CardTitle>
              <CardDescription>Your account details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-gradient-to-r from-[#EC1A23] to-[#561D1A] flex items-center justify-center text-white text-2xl font-bold">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <h3 className="text-xl font-semibold">{user?.name}</h3>
                  <p className="text-muted-foreground">{user?.email}</p>
                  <span className="inline-flex px-2 py-1 mt-2 text-xs font-medium bg-red-100 text-red-700 capitalize">
                    {user?.role}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                <div>
                  <Label className="text-muted-foreground">Account ID</Label>
                  <p className="font-mono text-sm mt-1">{user?.id?.slice(0, 8)}...</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Member Since</Label>
                  <p className="text-sm mt-1">{new Date(user?.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WhatsApp Tab */}
        <TabsContent value="whatsapp">
          <Card className="border-border rounded-none">
            <CardHeader>
              <CardTitle className="font-['Manrope']">WhatsApp Business API</CardTitle>
              <CardDescription>Configure Meta WhatsApp Business API credentials</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Phone Number ID</Label>
                <Input
                  value={whatsappConfig.phone_id}
                  onChange={(e) => setWhatsappConfig({ ...whatsappConfig, phone_id: e.target.value })}
                  placeholder="Enter your WhatsApp Phone Number ID"
                  className="rounded-none font-mono"
                  data-testid="whatsapp-phone-id-input"
                />
                <p className="text-xs text-muted-foreground">Found in your Meta Business Suite</p>
              </div>
              <div className="space-y-2">
                <Label>Access Token</Label>
                <Input
                  type="password"
                  value={whatsappConfig.access_token}
                  onChange={(e) => setWhatsappConfig({ ...whatsappConfig, access_token: e.target.value })}
                  placeholder="Enter your permanent access token"
                  className="rounded-none font-mono"
                  data-testid="whatsapp-token-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Verify Token</Label>
                <Input
                  value={whatsappConfig.verify_token}
                  onChange={(e) => setWhatsappConfig({ ...whatsappConfig, verify_token: e.target.value })}
                  className="rounded-none font-mono"
                  data-testid="whatsapp-verify-token-input"
                />
                <p className="text-xs text-muted-foreground">Used for webhook verification</p>
              </div>
              <div className="space-y-2">
                <Label>Webhook URL</Label>
                <Input
                  value={whatsappConfig.webhook_url}
                  readOnly
                  className="rounded-none font-mono bg-muted"
                />
                <p className="text-xs text-muted-foreground">Configure this URL in your Meta App settings</p>
              </div>
              <div className="pt-4">
                <Button 
                  onClick={handleSaveWhatsApp} 
                  className="btn-gradient rounded-none"
                  disabled={loading}
                  data-testid="save-whatsapp-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save WhatsApp Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border rounded-none mt-6">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Setup Instructions</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm max-w-none">
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                <li>Go to <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-[#EC1A23] hover:underline">Meta for Developers</a></li>
                <li>Create or select your app</li>
                <li>Add WhatsApp product to your app</li>
                <li>Get your Phone Number ID from WhatsApp &gt; API Setup</li>
                <li>Generate a permanent access token</li>
                <li>Configure the webhook URL above in your app settings</li>
                <li>Subscribe to messages webhook</li>
              </ol>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mall Info Tab */}
        <TabsContent value="mall">
          <Card className="border-border rounded-none">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Mall Information</CardTitle>
              <CardDescription>Basic information displayed to customers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Mall Name</Label>
                  <Input
                    value={mallConfig.name}
                    onChange={(e) => setMallConfig({ ...mallConfig, name: e.target.value })}
                    className="rounded-none"
                    data-testid="mall-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Phone</Label>
                  <Input
                    value={mallConfig.phone}
                    onChange={(e) => setMallConfig({ ...mallConfig, phone: e.target.value })}
                    className="rounded-none"
                    data-testid="mall-phone-input"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={mallConfig.address}
                    onChange={(e) => setMallConfig({ ...mallConfig, address: e.target.value })}
                    className="rounded-none"
                    data-testid="mall-address-input"
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={mallConfig.email}
                    onChange={(e) => setMallConfig({ ...mallConfig, email: e.target.value })}
                    className="rounded-none"
                    data-testid="mall-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Weekday Hours</Label>
                  <Input
                    value={mallConfig.opening_hours_weekday}
                    onChange={(e) => setMallConfig({ ...mallConfig, opening_hours_weekday: e.target.value })}
                    className="rounded-none"
                    data-testid="mall-weekday-hours-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Weekend Hours</Label>
                  <Input
                    value={mallConfig.opening_hours_weekend}
                    onChange={(e) => setMallConfig({ ...mallConfig, opening_hours_weekend: e.target.value })}
                    className="rounded-none"
                    data-testid="mall-weekend-hours-input"
                  />
                </div>
              </div>
              <div className="pt-4">
                <Button 
                  onClick={handleSaveMall} 
                  className="btn-gradient rounded-none"
                  disabled={loading}
                  data-testid="save-mall-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Mall Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border rounded-none mt-6" data-testid="table-booking-mall-settings">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Table booking — mall notifications</CardTitle>
              <CardDescription>
                Notify mall management on every restaurant table request and status change (alongside restaurant and guest alerts)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Mall notify email</Label>
                  <Input
                    type="email"
                    value={tableBookingConfig.mall_email}
                    onChange={(e) => setTableBookingConfig({ ...tableBookingConfig, mall_email: e.target.value })}
                    placeholder="bookings@westgate.co.ke"
                    className="rounded-none"
                    data-testid="table-booking-mall-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mall notify WhatsApp</Label>
                  <Input
                    value={tableBookingConfig.mall_whatsapp}
                    onChange={(e) => setTableBookingConfig({ ...tableBookingConfig, mall_whatsapp: e.target.value })}
                    placeholder="2547XXXXXXXX"
                    className="rounded-none"
                    data-testid="table-booking-mall-whatsapp"
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={tableBookingConfig.notify_email}
                    onCheckedChange={(checked) => setTableBookingConfig({ ...tableBookingConfig, notify_email: checked })}
                    data-testid="table-booking-notify-email"
                  />
                  <Label>Email mall management</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={tableBookingConfig.notify_whatsapp}
                    onCheckedChange={(checked) => setTableBookingConfig({ ...tableBookingConfig, notify_whatsapp: checked })}
                    data-testid="table-booking-notify-whatsapp"
                  />
                  <Label>WhatsApp mall management</Label>
                </div>
              </div>
              <div className="pt-2">
                <Button
                  onClick={handleSaveTableBooking}
                  className="btn-gradient rounded-none"
                  disabled={loading}
                  data-testid="save-table-booking-settings-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save table booking notifications
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* External API Tab */}
        <TabsContent value="external-api">
          <div className="space-y-6">
            {/* Stores API */}
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope']">Stores API (PHP)</CardTitle>
                <CardDescription>Configure external PHP API endpoint to fetch store data</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>API Endpoint URL *</Label>
                    <Input
                      value={externalApiConfig.stores_api_url}
                      onChange={(e) => setExternalApiConfig({ ...externalApiConfig, stores_api_url: e.target.value })}
                      placeholder="https://your-mall.com/api/stores.php"
                      className="rounded-none font-mono"
                      data-testid="stores-api-url-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>API Key / Token (if required)</Label>
                    <Input
                      type="password"
                      value={externalApiConfig.stores_api_key}
                      onChange={(e) => setExternalApiConfig({ ...externalApiConfig, stores_api_key: e.target.value })}
                      placeholder="Enter API key"
                      className="rounded-none font-mono"
                      data-testid="stores-api-key-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>HTTP Method</Label>
                    <select
                      value={externalApiConfig.stores_api_method}
                      onChange={(e) => setExternalApiConfig({ ...externalApiConfig, stores_api_method: e.target.value })}
                      className="w-full h-10 px-3 rounded-none border border-input bg-background text-sm"
                      data-testid="stores-api-method-select"
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                    </select>
                  </div>
                </div>
                {storeApiStatus && (
                  <div className={`flex items-center gap-2 p-3 ${storeApiStatus.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                    {storeApiStatus.success ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    <span className="text-sm">{storeApiStatus.message}</span>
                  </div>
                )}
                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline"
                    onClick={testStoresApi} 
                    className="rounded-none"
                    disabled={testingStores}
                    data-testid="test-stores-api-btn"
                  >
                    {testingStores ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
                    Test Connection
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={syncStores} 
                    className="rounded-none"
                    disabled={loading || !storeApiStatus?.success}
                    data-testid="sync-stores-btn"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Sync Stores
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Restaurants API */}
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope']">Restaurants API (PHP)</CardTitle>
                <CardDescription>Configure external PHP API endpoint to fetch restaurant data</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>API Endpoint URL *</Label>
                    <Input
                      value={externalApiConfig.restaurants_api_url}
                      onChange={(e) => setExternalApiConfig({ ...externalApiConfig, restaurants_api_url: e.target.value })}
                      placeholder="https://your-mall.com/api/restaurants.php"
                      className="rounded-none font-mono"
                      data-testid="restaurants-api-url-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>API Key / Token (if required)</Label>
                    <Input
                      type="password"
                      value={externalApiConfig.restaurants_api_key}
                      onChange={(e) => setExternalApiConfig({ ...externalApiConfig, restaurants_api_key: e.target.value })}
                      placeholder="Enter API key"
                      className="rounded-none font-mono"
                      data-testid="restaurants-api-key-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>HTTP Method</Label>
                    <select
                      value={externalApiConfig.restaurants_api_method}
                      onChange={(e) => setExternalApiConfig({ ...externalApiConfig, restaurants_api_method: e.target.value })}
                      className="w-full h-10 px-3 rounded-none border border-input bg-background text-sm"
                      data-testid="restaurants-api-method-select"
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                    </select>
                  </div>
                </div>
                {restaurantApiStatus && (
                  <div className={`flex items-center gap-2 p-3 ${restaurantApiStatus.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                    {restaurantApiStatus.success ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    <span className="text-sm">{restaurantApiStatus.message}</span>
                  </div>
                )}
                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline"
                    onClick={testRestaurantsApi} 
                    className="rounded-none"
                    disabled={testingRestaurants}
                    data-testid="test-restaurants-api-btn"
                  >
                    {testingRestaurants ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
                    Test Connection
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={syncRestaurants} 
                    className="rounded-none"
                    disabled={loading || !restaurantApiStatus?.success}
                    data-testid="sync-restaurants-btn"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Sync Restaurants
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Save All Button */}
            <div className="flex justify-end">
              <Button 
                onClick={handleSaveExternalApi} 
                className="btn-gradient rounded-none"
                disabled={loading}
                data-testid="save-external-api-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                Save API Configuration
              </Button>
            </div>

            {/* Help Card */}
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope']">Expected API Response Format</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm max-w-none">
                <p className="text-sm text-muted-foreground mb-3">Your PHP API should return JSON in this format:</p>
                <pre className="bg-muted p-4 text-xs font-mono overflow-x-auto">
{`{
  "success": true,
  "data": [
    {
      "name": "Store Name",
      "category": "Fashion",
      "floor": "1",
      "unit_number": "G-15",
      "phone": "+1234567890",
      "email": "store@email.com",
      "opening_hours": "10:00 AM - 10:00 PM"
    }
  ]
}`}
                </pre>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* n8n Tab */}
        <TabsContent value="n8n">
          <div className="space-y-6">
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope']">n8n Workflow Integration</CardTitle>
                <CardDescription>Configure n8n webhook URLs to trigger automated workflows</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Switch
                    checked={n8nConfig.enable_webhooks}
                    onCheckedChange={(checked) => setN8nConfig({ ...n8nConfig, enable_webhooks: checked })}
                    data-testid="n8n-enable-switch"
                  />
                  <Label>Enable n8n Webhooks</Label>
                </div>
                
                <div className="space-y-2">
                  <Label>Booking Webhook URL</Label>
                  <Input
                    value={n8nConfig.booking_webhook}
                    onChange={(e) => setN8nConfig({ ...n8nConfig, booking_webhook: e.target.value })}
                    placeholder="https://your-n8n.com/webhook/booking"
                    className="rounded-none font-mono"
                    data-testid="n8n-booking-webhook-input"
                  />
                  <p className="text-xs text-muted-foreground">Triggered when a new movie or restaurant booking is created</p>
                </div>
                
                <div className="space-y-2">
                  <Label>WhatsApp Message Webhook URL</Label>
                  <Input
                    value={n8nConfig.whatsapp_webhook}
                    onChange={(e) => setN8nConfig({ ...n8nConfig, whatsapp_webhook: e.target.value })}
                    placeholder="https://your-n8n.com/webhook/whatsapp"
                    className="rounded-none font-mono"
                    data-testid="n8n-whatsapp-webhook-input"
                  />
                  <p className="text-xs text-muted-foreground">Triggered when a WhatsApp message is received and processed</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Daily Report Webhook URL</Label>
                  <div className="flex gap-2">
                    <Input
                      value={n8nConfig.daily_report_webhook}
                      onChange={(e) => setN8nConfig({ ...n8nConfig, daily_report_webhook: e.target.value })}
                      placeholder="https://your-n8n.com/webhook/daily-report"
                      className="rounded-none font-mono flex-1"
                      data-testid="n8n-daily-report-webhook-input"
                    />
                    <Button 
                      variant="outline"
                      onClick={triggerDailyReport}
                      className="rounded-none"
                      disabled={!n8nConfig.daily_report_webhook}
                      data-testid="trigger-daily-report-btn"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Test
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">For daily summary reports (can be scheduled in n8n)</p>
                </div>

                <div className="pt-4">
                  <Button 
                    onClick={handleSaveN8n} 
                    className="btn-gradient rounded-none"
                    disabled={loading}
                    data-testid="save-n8n-btn"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save n8n Configuration
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* n8n Workflow JSON Download */}
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope']">n8n Workflow Templates</CardTitle>
                <CardDescription>Download ready-to-import workflow JSON files for your n8n instance</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <a 
                    href="/api/n8n/workflow/booking" 
                    download="mall-assistant-booking-workflow.json"
                    className="block"
                  >
                    <Card className="border-border rounded-none hover:border-[#EC1A23] transition-colors cursor-pointer">
                      <CardContent className="p-4 text-center">
                        <Zap className="h-8 w-8 mx-auto mb-2 text-[#EC1A23]" />
                        <p className="font-medium">Booking Workflow</p>
                        <p className="text-xs text-muted-foreground">Send notifications on new bookings</p>
                      </CardContent>
                    </Card>
                  </a>
                  <a 
                    href="/api/n8n/workflow/whatsapp" 
                    download="mall-assistant-whatsapp-workflow.json"
                    className="block"
                  >
                    <Card className="border-border rounded-none hover:border-[#EC1A23] transition-colors cursor-pointer">
                      <CardContent className="p-4 text-center">
                        <MessageSquare className="h-8 w-8 mx-auto mb-2 text-[#EC1A23]" />
                        <p className="font-medium">WhatsApp Workflow</p>
                        <p className="text-xs text-muted-foreground">Log messages to spreadsheet</p>
                      </CardContent>
                    </Card>
                  </a>
                  <a 
                    href="/api/n8n/workflow/daily-report" 
                    download="mall-assistant-daily-report-workflow.json"
                    className="block"
                  >
                    <Card className="border-border rounded-none hover:border-[#EC1A23] transition-colors cursor-pointer">
                      <CardContent className="p-4 text-center">
                        <RefreshCw className="h-8 w-8 mx-auto mb-2 text-[#EC1A23]" />
                        <p className="font-medium">Daily Report Workflow</p>
                        <p className="text-xs text-muted-foreground">Send daily summary emails</p>
                      </CardContent>
                    </Card>
                  </a>
                </div>
                <p className="text-sm text-muted-foreground">
                  Import these JSON files into your n8n instance via Settings → Import Workflow
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* AI Tab */}
        <TabsContent value="ai">
          <div className="space-y-6">
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope']">AI Provider &amp; Models</CardTitle>
                <CardDescription>Enable AI-powered chatbot responses with curated models, sync, and rate limits (Meta WhatsApp compliant)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 mb-4 p-4 bg-muted">
                  <Switch
                    checked={aiConfig.enable_ai}
                    onCheckedChange={(checked) => setAiConfig({ ...aiConfig, enable_ai: checked })}
                    data-testid="ai-enable-switch"
                  />
                  <div>
                    <Label className="text-base">Enable AI-Powered Responses</Label>
                    <p className="text-xs text-muted-foreground">Uses the selected provider/model with mall-context safety filters</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Provider</Label>
                  <select
                    value={aiConfig.provider || "openai"}
                    onChange={(e) => {
                      const provider = e.target.value;
                      const models = aiConfig.recommended_models?.[provider] || [];
                      setAiConfig({
                        ...aiConfig,
                        provider,
                        model: models[0] || aiConfig.model,
                        base_url: provider === "custom" ? (aiConfig.base_url || "") : "",
                      });
                    }}
                    className="w-full h-10 px-3 rounded-none border border-input bg-background text-sm"
                    data-testid="ai-provider-select"
                  >
                    <option value="openai">OpenAI (GPT)</option>
                    <option value="groq">Groq (open models / GPT-OSS)</option>
                    <option value="openrouter">OpenRouter (open models, free options)</option>
                    <option value="together">Together AI (open models)</option>
                    <option value="ollama">Ollama (local open-source)</option>
                    <option value="custom">Custom OpenAI-compatible URL</option>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    Curated options: OpenAI GPT-6 / GPT-5.x, Groq GPT-OSS / Qwen, OpenRouter free Gemma/Nemotron, or local Ollama.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    value={aiConfig.api_key || aiConfig.openai_api_key || ""}
                    onChange={(e) => setAiConfig({
                      ...aiConfig,
                      api_key: e.target.value,
                      openai_api_key: e.target.value,
                    })}
                    placeholder={aiConfig.has_api_key ? "API key is configured (enter new key to change)" : (aiConfig.provider === "ollama" ? "optional for local Ollama" : "sk-... / gsk_...")}
                    className="rounded-none font-mono"
                    data-testid="ai-api-key-input"
                  />
                  {aiConfig.has_api_key && (
                    <p className="text-xs text-emerald-600 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> API key is configured
                    </p>
                  )}
                </div>

                {(aiConfig.provider === "custom" || aiConfig.provider === "ollama") && (
                  <div className="space-y-2">
                    <Label>Base URL {aiConfig.provider === "ollama" ? "(optional override)" : ""}</Label>
                    <Input
                      value={aiConfig.base_url || ""}
                      onChange={(e) => setAiConfig({ ...aiConfig, base_url: e.target.value })}
                      placeholder={aiConfig.provider === "ollama" ? "http://127.0.0.1:11434/v1" : "https://your-llm-host/v1"}
                      className="rounded-none font-mono"
                      data-testid="ai-base-url-input"
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label>Model</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={syncAiModels}
                      className="rounded-none h-8"
                      disabled={syncingAiModels}
                      data-testid="sync-ai-models-btn"
                    >
                      {syncingAiModels ? <RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-2" />}
                      Sync models
                    </Button>
                  </div>
                  <select
                    value={aiConfig.model}
                    onChange={(e) => setAiConfig({ ...aiConfig, model: e.target.value })}
                    className="w-full h-10 px-3 rounded-none border border-input bg-background text-sm"
                    data-testid="ai-model-select"
                  >
                    {(aiConfig.recommended_models?.[aiConfig.provider] || []).map((modelName) => (
                      <option key={modelName} value={modelName}>{modelName}</option>
                    ))}
                    {!(aiConfig.recommended_models?.[aiConfig.provider] || []).includes(aiConfig.model) && aiConfig.model ? (
                      <option value={aiConfig.model}>{aiConfig.model}</option>
                    ) : null}
                  </select>
                  <Input
                    value={aiConfig.model}
                    onChange={(e) => setAiConfig({ ...aiConfig, model: e.target.value })}
                    placeholder="Or type any model id"
                    className="rounded-none font-mono"
                    data-testid="ai-model-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Sync reloads the curated recommended list after a deploy. Save to persist your selection.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Max output tokens</Label>
                    <Input
                      type="number"
                      min={1}
                      value={aiConfig.max_output_tokens ?? 800}
                      onChange={(e) => setAiConfig({ ...aiConfig, max_output_tokens: e.target.value })}
                      className="rounded-none font-mono"
                      data-testid="ai-max-output-tokens-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Requests / minute</Label>
                    <Input
                      type="number"
                      min={1}
                      value={aiConfig.rate_limit_per_minute ?? 10}
                      onChange={(e) => setAiConfig({ ...aiConfig, rate_limit_per_minute: e.target.value })}
                      className="rounded-none font-mono"
                      data-testid="ai-rate-limit-minute-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Requests / day</Label>
                    <Input
                      type="number"
                      min={1}
                      value={aiConfig.rate_limit_per_day ?? 100}
                      onChange={(e) => setAiConfig({ ...aiConfig, rate_limit_per_day: e.target.value })}
                      className="rounded-none font-mono"
                      data-testid="ai-rate-limit-day-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Limits apply per client IP and per chat session (WhatsApp uses phone as the key). Admin Test Connection bypasses request limits.
                </p>

                <div className="p-3 bg-amber-50 text-amber-900 text-xs space-y-1">
                  <p className="font-medium">Safety (always on)</p>
                  <p>Blocks prompt-injection attempts and refuses illegal or age-restricted topics (child exploitation, drugs, sexual content, violence). Mall Q&amp;A only.</p>
                </div>

                {aiStatus && (
                  <div className={`flex items-center gap-2 p-3 ${aiStatus.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                    {aiStatus.success ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    <span className="text-sm">{aiStatus.message}</span>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button 
                    variant="outline"
                    onClick={testAiConnection}
                    className="rounded-none"
                    disabled={testingAi}
                    data-testid="test-ai-btn"
                  >
                    {testingAi ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Bot className="h-4 w-4 mr-2" />}
                    Test Connection
                  </Button>
                  <Button 
                    onClick={handleSaveAi} 
                    className="btn-gradient rounded-none"
                    disabled={loading}
                    data-testid="save-ai-btn"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save AI Configuration
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* AI Info Card */}
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope']">How AI Chatbot Works</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm max-w-none">
                <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
                  <li><strong>Multi-provider:</strong> OpenAI GPT-6 / GPT-5.x, Groq/OpenRouter/Together open models, or local Ollama</li>
                  <li><strong>Sync models:</strong> Reload curated recommended model IDs after a deploy without losing your saved selection when still listed</li>
                  <li><strong>Rate limits:</strong> Configurable max output tokens and per-IP / per-session request caps</li>
                  <li><strong>Context-Aware:</strong> AI knows your stores, restaurants, services, movies, and mall hours</li>
                  <li><strong>Safety:</strong> Prompt-injection resistance + refusal of illegal / age-restricted topics</li>
                  <li><strong>Meta Compliant:</strong> Identifies as AI assistant, does not collect sensitive data</li>
                  <li><strong>Fallback:</strong> Rule-based replies when AI is disabled</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Westgate API Tab */}
        <TabsContent value="westgate">
          <div className="space-y-6">
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope']">Westgate Mall API Integration</CardTitle>
                <CardDescription>Connect to Westgate Mall's CMS API to fetch stores, restaurants, and cinema data</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>API Base URL</Label>
                  <Input
                    value={westgateConfig.base_url}
                    onChange={(e) => setWestgateConfig({ ...westgateConfig, base_url: e.target.value })}
                    placeholder="https://cms.westgate.co.ke"
                    className="rounded-none font-mono"
                    data-testid="westgate-base-url-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={westgateConfig.email}
                    onChange={(e) => setWestgateConfig({ ...westgateConfig, email: e.target.value })}
                    placeholder="your-email@example.com"
                    className="rounded-none"
                    data-testid="westgate-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={westgateConfig.password}
                    onChange={(e) => setWestgateConfig({ ...westgateConfig, password: e.target.value })}
                    placeholder={westgateConfig.has_credentials ? "Password configured (enter new to change)" : "Enter password"}
                    className="rounded-none"
                    data-testid="westgate-password-input"
                  />
                  {westgateConfig.has_credentials && (
                    <p className="text-xs text-emerald-600 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" /> Credentials configured
                    </p>
                  )}
                </div>

                {westgateStatus && (
                  <div className={`flex items-center gap-2 p-3 ${westgateStatus.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                    {westgateStatus.success ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    <span className="text-sm">{westgateStatus.message}</span>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <Button 
                    variant="outline"
                    onClick={testWestgateConnection}
                    className="rounded-none"
                    disabled={testingWestgate}
                    data-testid="test-westgate-btn"
                  >
                    {testingWestgate ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
                    Test Connection
                  </Button>
                  <Button 
                    onClick={handleSaveWestgate} 
                    className="btn-gradient rounded-none"
                    disabled={loading}
                    data-testid="save-westgate-btn"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Configuration
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Auto Sync Status */}
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope']">Automatic Catalog Sync</CardTitle>
                <CardDescription>
                  Stores &amp; restaurants sync from api.westgate.co.ke; movies sync from Vista via Westgate Cinema API.
                  Runs on a schedule so manual sync is optional.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="p-3 bg-muted/40">
                    <p className="text-muted-foreground">Scheduler</p>
                    <p className="font-medium">
                      {autoSyncStatus?.enabled ? "Enabled" : "Disabled"}{" "}
                      {autoSyncStatus?.scheduler_running ? "(running)" : "(stopped)"}
                    </p>
                  </div>
                  <div className="p-3 bg-muted/40">
                    <p className="text-muted-foreground">Interval</p>
                    <p className="font-medium">{autoSyncStatus?.interval_minutes || 30} minutes</p>
                  </div>
                  <div className="p-3 bg-muted/40">
                    <p className="text-muted-foreground">Last run</p>
                    <p className="font-medium">{autoSyncStatus?.last_run_at || "Never"}</p>
                  </div>
                  <div className="p-3 bg-muted/40">
                    <p className="text-muted-foreground">Last success</p>
                    <p className="font-medium">{autoSyncStatus?.last_success_at || "Never"}</p>
                  </div>
                </div>
                {autoSyncStatus?.directory?.message && (
                  <p className="text-sm text-muted-foreground">Directory: {autoSyncStatus.directory.message}</p>
                )}
                {autoSyncStatus?.movies?.message && (
                  <p className="text-sm text-muted-foreground">Movies: {autoSyncStatus.movies.message}</p>
                )}
                {autoSyncStatus?.last_error && (
                  <p className="text-sm text-red-600">Last error: {autoSyncStatus.last_error}</p>
                )}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="rounded-none"
                    onClick={fetchAutoSyncStatus}
                    data-testid="refresh-auto-sync-btn"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh status
                  </Button>
                  <Button
                    className="btn-gradient rounded-none"
                    onClick={runAutoSyncNow}
                    disabled={runningAutoSync || autoSyncStatus?.running}
                    data-testid="run-auto-sync-btn"
                  >
                    {runningAutoSync || autoSyncStatus?.running ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Run sync now
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Sync Data Card */}
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope']">Manual Sync (optional)</CardTitle>
                <CardDescription>
                  Manual import remains available. Auto-sync already keeps stores, restaurants, and Vista movies updated.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="border-border rounded-none">
                    <CardContent className="p-4">
                      <div className="text-center mb-4">
                        <Database className="h-8 w-8 mx-auto mb-2 text-[#EC1A23]" />
                        <p className="font-medium">Stores</p>
                        <p className="text-xs text-muted-foreground">Store directory</p>
                      </div>
                      <Button 
                        variant="outline"
                        onClick={syncWestgateStores}
                        className="w-full rounded-none"
                        disabled={syncingStores}
                        data-testid="sync-westgate-stores-btn"
                      >
                        {syncingStores ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                        Sync Stores
                      </Button>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-border rounded-none">
                    <CardContent className="p-4">
                      <div className="text-center mb-4">
                        <Zap className="h-8 w-8 mx-auto mb-2 text-[#EC1A23]" />
                        <p className="font-medium">Restaurants</p>
                        <p className="text-xs text-muted-foreground">Dining options</p>
                      </div>
                      <Button 
                        variant="outline"
                        onClick={syncWestgateRestaurants}
                        className="w-full rounded-none"
                        disabled={syncingRestaurants}
                        data-testid="sync-westgate-restaurants-btn"
                      >
                        {syncingRestaurants ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                        Sync Restaurants
                      </Button>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-border rounded-none">
                    <CardContent className="p-4">
                      <div className="text-center mb-4">
                        <Play className="h-8 w-8 mx-auto mb-2 text-[#EC1A23]" />
                        <p className="font-medium">Movies</p>
                        <p className="text-xs text-muted-foreground">Vista / Cinema</p>
                      </div>
                      <Button 
                        variant="outline"
                        onClick={syncWestgateMovies}
                        className="w-full rounded-none"
                        disabled={syncingMovies}
                        data-testid="sync-westgate-movies-btn"
                      >
                        {syncingMovies ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                        Sync Movies
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>

            {/* API Endpoints Info */}
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope']">Available Westgate API Endpoints</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm max-w-none">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-semibold mb-2">Stores & Restaurants</p>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                      <li><code>/api/westgate/stores</code> - All stores</li>
                      <li><code>/api/westgate/restaurants</code> - Restaurants</li>
                      <li><code>/api/westgate/shops</code> - Shops</li>
                      <li><code>/api/westgate/services</code> - Services</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold mb-2">Cinema</p>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                      <li><code>/api/westgate/cinema/films</code> - Movies</li>
                      <li><code>/api/westgate/cinema/sessions</code> - Showtimes</li>
                      <li><code>/api/westgate/cinema/tickets</code> - Prices</li>
                      <li><code>/api/westgate/cinema/seats</code> - Seat plan</li>
                      <li><code>/api/westgate/cinema/reserve</code> - Reserve seats</li>
                      <li><code>/api/westgate/snacks</code> - Snack menu</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* M-Pesa Tab */}
        <TabsContent value="mpesa">
          <div className="space-y-6">
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-green-600" />
                  M-Pesa Daraja API Configuration
                </CardTitle>
                <CardDescription>
                  Configure M-Pesa STK Push for movie ticket payments
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="mpesa_consumer_key">Consumer Key</Label>
                    <Input
                      id="mpesa_consumer_key"
                      value={mpesaConfig.consumer_key}
                      onChange={(e) => setMpesaConfig({...mpesaConfig, consumer_key: e.target.value})}
                      placeholder="Your Safaricom Consumer Key"
                      className="rounded-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mpesa_consumer_secret">Consumer Secret</Label>
                    <Input
                      id="mpesa_consumer_secret"
                      type="password"
                      value={mpesaConfig.consumer_secret}
                      onChange={(e) => setMpesaConfig({...mpesaConfig, consumer_secret: e.target.value})}
                      placeholder="Your Safaricom Consumer Secret"
                      className="rounded-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mpesa_shortcode">Business Shortcode</Label>
                    <Input
                      id="mpesa_shortcode"
                      value={mpesaConfig.shortcode}
                      onChange={(e) => setMpesaConfig({...mpesaConfig, shortcode: e.target.value})}
                      placeholder="174379"
                      className="rounded-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mpesa_passkey">Passkey</Label>
                    <Input
                      id="mpesa_passkey"
                      type="password"
                      value={mpesaConfig.passkey}
                      onChange={(e) => setMpesaConfig({...mpesaConfig, passkey: e.target.value})}
                      placeholder="Lipa Na M-Pesa Passkey"
                      className="rounded-none"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="mpesa_callback">Callback URL</Label>
                    <Input
                      id="mpesa_callback"
                      value={mpesaConfig.callback_url}
                      onChange={(e) => setMpesaConfig({...mpesaConfig, callback_url: e.target.value})}
                      placeholder={`${API_URL}/api/public/mpesa/callback`}
                      className="rounded-none"
                    />
                    <p className="text-xs text-muted-foreground">
                      This URL must be publicly accessible. Use: <code className="bg-muted px-1">{API_URL}/api/public/mpesa/callback</code>
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Environment</Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="mpesa_env"
                          value="sandbox"
                          checked={mpesaConfig.environment === "sandbox"}
                          onChange={(e) => setMpesaConfig({...mpesaConfig, environment: e.target.value})}
                        />
                        <span>Sandbox (Test)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="mpesa_env"
                          value="production"
                          checked={mpesaConfig.environment === "production"}
                          onChange={(e) => setMpesaConfig({...mpesaConfig, environment: e.target.value})}
                        />
                        <span>Production (Live)</span>
                      </label>
                    </div>
                  </div>
                </div>
                <Button onClick={handleSaveMpesa} disabled={loading} className="rounded-none bg-green-600 hover:bg-green-700">
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? "Saving..." : "Save M-Pesa Configuration"}
                </Button>
              </CardContent>
            </Card>

            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope']">Getting M-Pesa Credentials</CardTitle>
              </CardHeader>
              <CardContent className="prose prose-sm max-w-none">
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                  <li>Go to <a href="https://developer.safaricom.co.ke" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">developer.safaricom.co.ke</a></li>
                  <li>Create an account or login</li>
                  <li>Create a new app for "Lipa Na M-Pesa Online"</li>
                  <li>Copy your Consumer Key and Consumer Secret</li>
                  <li>For sandbox testing, use shortcode: <code className="bg-muted px-1">174379</code></li>
                  <li>For production, apply for API access via Safaricom business</li>
                </ol>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* SMS API Tab */}
        <TabsContent value="sms">
          <div className="space-y-6">
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-blue-600" />
                  SMS API Configuration (Onfonmedia)
                </CardTitle>
                <CardDescription>
                  Configure SMS API for OTP verification and notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sms_url">SMS API URL</Label>
                    <Input
                      id="sms_url"
                      value={smsConfig.sms_url}
                      onChange={(e) => setSmsConfig({...smsConfig, sms_url: e.target.value})}
                      placeholder="https://apis.onfonmedia.co.ke/v1/sms/SendBulkSMS"
                      className="rounded-none"
                    />
                    <p className="text-xs text-gray-500">Authorization URL: https://apis.onfonmedia.co.ke/v1/authorization</p>
                  </div>

                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">Authorization Credentials</p>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="sms_api_username">API Username</Label>
                        <Input
                          id="sms_api_username"
                          value={smsConfig.api_username}
                          onChange={(e) => setSmsConfig({...smsConfig, api_username: e.target.value})}
                          placeholder="Enter API Username"
                          className="rounded-none"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="sms_api_password">API Password</Label>
                        <Input
                          id="sms_api_password"
                          type="password"
                          value={smsConfig.api_password}
                          onChange={(e) => setSmsConfig({...smsConfig, api_password: e.target.value})}
                          placeholder="Enter API Password"
                          className="rounded-none"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">API Headers</p>
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="sms_api_key">API Key</Label>
                        <Input
                          id="sms_api_key"
                          type="password"
                          value={smsConfig.api_key}
                          onChange={(e) => setSmsConfig({...smsConfig, api_key: e.target.value})}
                          placeholder="Enter API Key"
                          className="rounded-none"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="sms_client_id">Client ID</Label>
                        <Input
                          id="sms_client_id"
                          type="password"
                          value={smsConfig.client_id}
                          onChange={(e) => setSmsConfig({...smsConfig, client_id: e.target.value})}
                          placeholder="Enter Client ID"
                          className="rounded-none"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="sms_access_key">Access Key</Label>
                        <Input
                          id="sms_access_key"
                          type="password"
                          value={smsConfig.access_key}
                          onChange={(e) => setSmsConfig({...smsConfig, access_key: e.target.value})}
                          placeholder="Enter Access Key"
                          className="rounded-none"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <div className="space-y-2">
                      <Label htmlFor="sms_sender_id">Sender ID</Label>
                      <Input
                        id="sms_sender_id"
                        value={smsConfig.sender_id}
                        onChange={(e) => setSmsConfig({...smsConfig, sender_id: e.target.value})}
                        placeholder="WESTGATE"
                        className="rounded-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {smsConfig.is_configured ? (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm">SMS API configured</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-600">
                      <XCircle className="h-4 w-4" />
                      <span className="text-sm">SMS API not configured</span>
                    </div>
                  )}
                </div>

                <Button onClick={handleSaveSms} disabled={loading} className="rounded-none bg-green-600 hover:bg-green-700">
                  <Save className="h-4 w-4 mr-2" />
                  Save SMS Configuration
                </Button>
              </CardContent>
            </Card>

            {/* Test SMS */}
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg">Test SMS API</CardTitle>
                <CardDescription>
                  Send a test SMS to verify the configuration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Label htmlFor="test_phone">Phone Number</Label>
                    <Input
                      id="test_phone"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                      placeholder="254712345678"
                      className="rounded-none mt-1"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={testSmsApi} 
                      disabled={testingSms || !testPhone}
                      className="rounded-none"
                    >
                      {testingSms ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          Send Test SMS
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Instructions */}
            <Card className="border-border rounded-none bg-blue-50">
              <CardContent className="pt-6">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Zap className="h-4 w-4 text-blue-600" />
                  Onfonmedia SMS API Setup
                </h4>
                <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                  <li>Sign up at <a href="https://onfonmedia.co.ke" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">onfonmedia.co.ke</a></li>
                  <li>Get your API Username and API Password from the dashboard</li>
                  <li>Authorization endpoint: <code className="bg-white px-1 rounded">https://apis.onfonmedia.co.ke/v1/authorization</code></li>
                  <li>Configure your Sender ID (approved by the provider)</li>
                  <li>Test the connection by sending a test SMS</li>
                </ol>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Email Tab */}
        <TabsContent value="email">
          <div className="space-y-6">
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <Mail className="h-5 w-5 text-indigo-600" />
                  Email Configuration
                </CardTitle>
                <CardDescription>
                  Configure email for OTP verification and notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4">
                  {/* Provider Selection */}
                  <div className="space-y-2">
                    <Label>Email Provider</Label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="provider"
                          value="microsoft365"
                          checked={emailConfig.provider === "microsoft365"}
                          onChange={(e) => setEmailConfig({
                            ...emailConfig, 
                            provider: e.target.value,
                            smtp_host: "smtp.office365.com",
                            smtp_port: 587,
                            use_tls: true,
                            use_ssl: false
                          })}
                          className="text-[#EC1A23]"
                        />
                        <span className="flex items-center gap-2">
                          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#0078D4">
                            <path d="M21.17 2.06A2.5 2.5 0 0019.5 2H4.5a2.5 2.5 0 00-2.5 2.5v15a2.5 2.5 0 002.5 2.5h15a2.5 2.5 0 002.5-2.5v-15a2.5 2.5 0 00-.83-1.94zM12 5l7 4.5v9l-7-4.5V5zm-1 9.5L4 19v-9l7 4.5z"/>
                          </svg>
                          Microsoft 365
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="provider"
                          value="generic"
                          checked={emailConfig.provider === "generic"}
                          onChange={(e) => setEmailConfig({
                            ...emailConfig, 
                            provider: e.target.value,
                            smtp_host: "",
                            smtp_port: 587
                          })}
                          className="text-[#EC1A23]"
                        />
                        <span>Generic SMTP</span>
                      </label>
                    </div>
                  </div>

                  {emailConfig.provider === "microsoft365" && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-800">
                        <strong>Microsoft 365 SMTP Settings:</strong><br/>
                        Server: smtp.office365.com | Port: 587 | TLS: Enabled
                      </p>
                    </div>
                  )}

                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">SMTP Settings</p>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="smtp_host">SMTP Host</Label>
                        <Input
                          id="smtp_host"
                          value={emailConfig.smtp_host}
                          onChange={(e) => setEmailConfig({...emailConfig, smtp_host: e.target.value})}
                          placeholder={emailConfig.provider === "microsoft365" ? "smtp.office365.com" : "smtp.example.com"}
                          className="rounded-none"
                          disabled={emailConfig.provider === "microsoft365"}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="smtp_port">SMTP Port</Label>
                        <Input
                          id="smtp_port"
                          type="number"
                          value={emailConfig.smtp_port}
                          onChange={(e) => setEmailConfig({...emailConfig, smtp_port: parseInt(e.target.value)})}
                          placeholder="587"
                          className="rounded-none"
                          disabled={emailConfig.provider === "microsoft365"}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">Authentication</p>
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="smtp_username">Username / Email</Label>
                        <Input
                          id="smtp_username"
                          value={emailConfig.smtp_username}
                          onChange={(e) => setEmailConfig({...emailConfig, smtp_username: e.target.value})}
                          placeholder="your-email@domain.com"
                          className="rounded-none"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="smtp_password">Password / App Password</Label>
                        <Input
                          id="smtp_password"
                          type="password"
                          value={emailConfig.smtp_password}
                          onChange={(e) => setEmailConfig({...emailConfig, smtp_password: e.target.value})}
                          placeholder="Enter password or app password"
                          className="rounded-none"
                        />
                        {emailConfig.provider === "microsoft365" && (
                          <p className="text-xs text-gray-500">
                            Use an App Password if you have MFA enabled. 
                            <a href="https://support.microsoft.com/en-us/account-billing/manage-app-passwords-for-two-step-verification-d6dc8c6d-4bf7-4851-ad95-6d07799387e9" target="_blank" rel="noopener noreferrer" className="text-blue-600 ml-1 hover:underline">Learn more</a>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">Sender Details</p>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="from_email">From Email</Label>
                        <Input
                          id="from_email"
                          type="email"
                          value={emailConfig.from_email}
                          onChange={(e) => setEmailConfig({...emailConfig, from_email: e.target.value})}
                          placeholder="noreply@westgate.co.ke"
                          className="rounded-none"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="from_name">From Name</Label>
                        <Input
                          id="from_name"
                          value={emailConfig.from_name}
                          onChange={(e) => setEmailConfig({...emailConfig, from_name: e.target.value})}
                          placeholder="Westgate Mall"
                          className="rounded-none"
                        />
                      </div>
                    </div>
                  </div>

                  {emailConfig.provider === "generic" && (
                    <div className="border-t pt-4">
                      <p className="text-sm font-medium text-gray-700 mb-3">Security</p>
                      <div className="flex gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={emailConfig.use_tls}
                            onChange={(e) => setEmailConfig({...emailConfig, use_tls: e.target.checked, use_ssl: e.target.checked ? false : emailConfig.use_ssl})}
                            className="rounded"
                          />
                          <span className="text-sm">Use TLS (STARTTLS)</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={emailConfig.use_ssl}
                            onChange={(e) => setEmailConfig({...emailConfig, use_ssl: e.target.checked, use_tls: e.target.checked ? false : emailConfig.use_tls})}
                            className="rounded"
                          />
                          <span className="text-sm">Use SSL</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {emailConfig.is_configured ? (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm">Email configured</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-600">
                      <XCircle className="h-4 w-4" />
                      <span className="text-sm">Email not configured</span>
                    </div>
                  )}
                </div>

                <Button onClick={handleSaveEmail} disabled={loading} className="rounded-none bg-green-600 hover:bg-green-700">
                  <Save className="h-4 w-4 mr-2" />
                  Save Email Configuration
                </Button>
              </CardContent>
            </Card>

            {/* Test Email */}
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope'] text-lg">Test Email</CardTitle>
                <CardDescription>
                  Send a test email to verify the configuration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Label htmlFor="test_email">Email Address</Label>
                    <Input
                      id="test_email"
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="test@example.com"
                      className="rounded-none mt-1"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={testEmailApi} 
                      disabled={testingEmail || !testEmail}
                      className="rounded-none"
                    >
                      {testingEmail ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          Send Test Email
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Instructions */}
            <Card className="border-border rounded-none bg-indigo-50">
              <CardContent className="pt-6">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Zap className="h-4 w-4 text-indigo-600" />
                  Email Setup Guide
                </h4>
                <div className="space-y-4 text-sm text-muted-foreground">
                  <div>
                    <p className="font-medium text-gray-700">Microsoft 365:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Use your Microsoft 365 email as username</li>
                      <li>Generate an App Password if MFA is enabled</li>
                      <li>SMTP Host: smtp.office365.com, Port: 587</li>
                    </ol>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">Gmail:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Enable 2FA and create an App Password</li>
                      <li>SMTP Host: smtp.gmail.com, Port: 587 (TLS) or 465 (SSL)</li>
                    </ol>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">Generic SMTP:</p>
                    <ol className="list-decimal list-inside space-y-1 ml-2">
                      <li>Get SMTP credentials from your email provider</li>
                      <li>Common ports: 587 (TLS), 465 (SSL), 25 (unencrypted)</li>
                    </ol>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="mailchimp">
          <div className="space-y-6">
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <Megaphone className="h-5 w-5 text-[#EC1A23]" />
                  Mailchimp Audience Sync
                </CardTitle>
                <CardDescription>
                  Sync verified chatbot visitors to a Mailchimp audience after OTP signup
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <Label>Enable Mailchimp sync</Label>
                    <p className="text-xs text-muted-foreground">New verified visitors are added to the audience</p>
                  </div>
                  <Switch
                    checked={mailchimpConfig.enabled}
                    onCheckedChange={(checked) => setMailchimpConfig({ ...mailchimpConfig, enabled: checked })}
                    data-testid="mailchimp-enabled"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mailchimp_api_key">API Key</Label>
                  <Input
                    id="mailchimp_api_key"
                    type="password"
                    value={mailchimpConfig.api_key}
                    onChange={(e) => setMailchimpConfig({ ...mailchimpConfig, api_key: e.target.value })}
                    placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx-us21"
                    className="rounded-none"
                    data-testid="mailchimp-api-key"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="mailchimp_server">Server prefix</Label>
                    <Input
                      id="mailchimp_server"
                      value={mailchimpConfig.server_prefix}
                      onChange={(e) => setMailchimpConfig({ ...mailchimpConfig, server_prefix: e.target.value })}
                      placeholder="us21"
                      className="rounded-none"
                      data-testid="mailchimp-server-prefix"
                    />
                    <p className="text-xs text-muted-foreground">The suffix after the dash in your API key. Leave blank to use the key suffix.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mailchimp_audience">Audience / List ID</Label>
                    <Input
                      id="mailchimp_audience"
                      value={mailchimpConfig.audience_id}
                      onChange={(e) => setMailchimpConfig({ ...mailchimpConfig, audience_id: e.target.value })}
                      placeholder="Audience ID from Mailchimp"
                      className="rounded-none"
                      data-testid="mailchimp-audience-id"
                    />
                  </div>
                </div>
                {mailchimpStatus && (
                  <div className={`p-3 rounded-lg text-sm ${mailchimpStatus.success ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`}>
                    {mailchimpStatus.message}
                  </div>
                )}
                <div className="flex flex-wrap gap-3">
                  <Button onClick={handleSaveMailchimp} disabled={loading} className="btn-gradient rounded-none" data-testid="mailchimp-save">
                    <Save className="h-4 w-4 mr-2" />
                    Save Mailchimp
                  </Button>
                  <Button
                    variant="outline"
                    onClick={testMailchimpApi}
                    disabled={testingMailchimp}
                    className="rounded-none"
                    data-testid="mailchimp-test"
                  >
                    {testingMailchimp ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                    Test Connection
                  </Button>
                  <Button
                    variant="outline"
                    onClick={syncMailchimpVisitors}
                    disabled={syncingMailchimp}
                    className="rounded-none"
                    data-testid="mailchimp-sync"
                  >
                    {syncingMailchimp ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
                    Sync existing visitors
                  </Button>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border rounded-none bg-blue-50">
              <CardContent className="pt-6">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Zap className="h-4 w-4 text-blue-600" />
                  Mailchimp setup
                </h4>
                <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                  <li>In Mailchimp, open Account → Extras → API keys and create a key.</li>
                  <li>Copy the key. The part after the last dash is the server prefix (for example us21).</li>
                  <li>Open Audience → Settings → Audience name and defaults, and copy the Audience ID.</li>
                  <li>Save here, then Test Connection.</li>
                  <li>Chatbot signup collects T&amp;C / Privacy / Cookies acceptance and channel opt-ins (email, SMS, WhatsApp).</li>
                  <li>After phone OTP, visitors with marketing channels get an email opt-in link. On confirm they are subscribed here, receive a welcome email, and can get periodic Mailchimp campaigns.</li>
                  <li>Use Sync Visitors to push confirmed opt-ins only. Create campaign/automation journeys in Mailchimp for ongoing deals and mall updates.</li>
                </ol>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="tickets">
          <div className="space-y-6">
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <Ticket className="h-5 w-5 text-[#EC1A23]" />
                  Ticket delivery after M-Pesa
                </CardTitle>
                <CardDescription>
                  After a verified M-Pesa callback, send one admit-one ticket per seat plus a KRA eTIMS-style receipt
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <Label>Email tickets + tax invoice</Label>
                    <p className="text-xs text-muted-foreground">Sent to the email on the booking</p>
                  </div>
                  <Switch
                    checked={ticketDelivery.email_enabled}
                    onCheckedChange={(checked) => setTicketDelivery({ ...ticketDelivery, email_enabled: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <Label>WhatsApp to customer phone</Label>
                    <p className="text-xs text-muted-foreground">
                      {ticketDelivery.whatsapp_ready ? "WhatsApp Business API is configured" : "Set WhatsApp token and phone ID in the WhatsApp tab / env first"}
                    </p>
                  </div>
                  <Switch
                    checked={ticketDelivery.whatsapp_enabled}
                    onCheckedChange={(checked) => setTicketDelivery({ ...ticketDelivery, whatsapp_enabled: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <Label>Telegram bot / channel</Label>
                    <p className="text-xs text-muted-foreground">Sends a copy to the Westgate Cinema chat or channel</p>
                  </div>
                  <Switch
                    checked={ticketDelivery.telegram_enabled}
                    onCheckedChange={(checked) => setTicketDelivery({ ...ticketDelivery, telegram_enabled: checked })}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Telegram bot token</Label>
                    <Input
                      type="password"
                      value={ticketDelivery.telegram_bot_token}
                      onChange={(e) => setTicketDelivery({ ...ticketDelivery, telegram_bot_token: e.target.value })}
                      placeholder="123456:ABC-DEF..."
                      className="rounded-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telegram chat / channel ID</Label>
                    <Input
                      value={ticketDelivery.telegram_chat_id}
                      onChange={(e) => setTicketDelivery({ ...ticketDelivery, telegram_chat_id: e.target.value })}
                      placeholder="-1001234567890"
                      className="rounded-none"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Seller name (eTIMS)</Label>
                  <Input
                    value={ticketDelivery.seller_name}
                    onChange={(e) => setTicketDelivery({ ...ticketDelivery, seller_name: e.target.value })}
                    className="rounded-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Seller address</Label>
                  <Input
                    value={ticketDelivery.seller_address}
                    onChange={(e) => setTicketDelivery({ ...ticketDelivery, seller_address: e.target.value })}
                    className="rounded-none"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>KRA PIN</Label>
                    <Input
                      value={ticketDelivery.kra_pin}
                      onChange={(e) => setTicketDelivery({ ...ticketDelivery, kra_pin: e.target.value })}
                      placeholder="P051234567X"
                      className="rounded-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>eTIMS CU serial</Label>
                    <Input
                      value={ticketDelivery.cu_serial}
                      onChange={(e) => setTicketDelivery({ ...ticketDelivery, cu_serial: e.target.value })}
                      placeholder="OSCU / VSCU serial"
                      className="rounded-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>VAT %</Label>
                    <Input
                      type="number"
                      value={ticketDelivery.vat_rate_percent}
                      onChange={(e) => setTicketDelivery({ ...ticketDelivery, vat_rate_percent: e.target.value })}
                      className="rounded-none"
                    />
                  </div>
                </div>
                <Button onClick={handleSaveTicketDelivery} disabled={loading} className="btn-gradient rounded-none">
                  <Save className="h-4 w-4 mr-2" />
                  Save ticket delivery
                </Button>
              </CardContent>
            </Card>
            <Card className="border-border rounded-none bg-blue-50">
              <CardContent className="pt-6">
                <h4 className="font-semibold mb-3">Create a Telegram bot for Westgate Cinema</h4>
                <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                  <li>Open Telegram and search <strong>@BotFather</strong>.</li>
                  <li>Send <code>/newbot</code>. Name it <strong>Westgate Cinema Tickets</strong>. Username example: <code>westgate_cinema_tickets_bot</code>.</li>
                  <li>Copy the bot token BotFather gives you and paste it above.</li>
                  <li>Create a private channel or group named <strong>Westgate Cinema Tickets</strong>. Add the bot as an administrator (Post messages).</li>
                  <li>Get the chat ID: add <code>@userinfobot</code> in the channel, or call <code>https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code> after posting a message, and copy <code>chat.id</code> (channels start with <code>-100</code>).</li>
                  <li>Paste the chat ID, enable Telegram, and Save. After M-Pesa confirms, each seat QR is posted there. Customers still get Email and WhatsApp on their own phone.</li>
                </ol>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Embed Widget Tab */}
        <TabsContent value="s3">
          <Card className="border-border rounded-none">
            <CardHeader>
              <CardTitle className="font-['Manrope']">AWS S3 asset bucket</CardTitle>
              <CardDescription>
                Snack photos uploaded in CMS go to this bucket when enabled. App JS/CSS still ships from the frontend container.
                Store logos and movie posters continue to load from Westgate CMS. Leave disabled to keep uploads on this server under data/backend-uploads.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2 p-4 bg-muted">
                <Switch
                  checked={s3Config.enabled}
                  onCheckedChange={(checked) => setS3Config({ ...s3Config, enabled: checked })}
                  data-testid="s3-enable-switch"
                />
                <div>
                  <Label className="text-base">Store new snack uploads on S3</Label>
                  <p className="text-sm text-muted-foreground">
                    Current storage: {s3Config.enabled ? "Amazon S3" : "host disk (data/backend-uploads)"}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>AWS Access Key ID</Label>
                  <Input
                    value={s3Config.access_key_id}
                    onChange={(e) => setS3Config({ ...s3Config, access_key_id: e.target.value })}
                    placeholder={s3Config.is_configured ? "Key is saved (enter a new key to change)" : "AKIA..."}
                    data-testid="s3-access-key"
                  />
                </div>
                <div className="space-y-2">
                  <Label>AWS Secret Access Key</Label>
                  <Input
                    type="password"
                    value={s3Config.secret_access_key}
                    onChange={(e) => setS3Config({ ...s3Config, secret_access_key: e.target.value })}
                    placeholder="Secret key"
                    data-testid="s3-secret-key"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Region</Label>
                  <Input
                    value={s3Config.region}
                    onChange={(e) => setS3Config({ ...s3Config, region: e.target.value })}
                    placeholder="af-south-1"
                    data-testid="s3-region"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bucket name</Label>
                  <Input
                    value={s3Config.bucket}
                    onChange={(e) => setS3Config({ ...s3Config, bucket: e.target.value })}
                    placeholder="westgate-chatbot-assets"
                    data-testid="s3-bucket"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Key prefix</Label>
                  <Input
                    value={s3Config.prefix}
                    onChange={(e) => setS3Config({ ...s3Config, prefix: e.target.value })}
                    placeholder="assets"
                    data-testid="s3-prefix"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Public base URL (optional)</Label>
                  <Input
                    value={s3Config.public_base_url}
                    onChange={(e) => setS3Config({ ...s3Config, public_base_url: e.target.value })}
                    placeholder="https://cdn.example.com"
                    data-testid="s3-public-base"
                  />
                  <p className="text-xs text-muted-foreground">Use a CloudFront or custom domain. If empty, objects use the regional S3 website URL.</p>
                </div>
              </div>
              {s3Status && (
                <div className={`p-3 text-sm ${s3Status.success ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}>
                  {s3Status.message}
                  {s3Status.example ? <div className="mt-1 break-all">{s3Status.example}</div> : null}
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={handleSaveS3} disabled={loading} className="rounded-none bg-[#EC1A23] hover:bg-[#c0161d]" data-testid="save-s3">
                  <Save className="h-4 w-4 mr-2" />
                  Save S3 settings
                </Button>
                <Button type="button" variant="outline" onClick={testS3Connection} disabled={testingS3} className="rounded-none" data-testid="test-s3">
                  {testingS3 ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                  Test connection
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="embed">
          <div className="space-y-6">
            <Card className="border-border rounded-none">
              <CardHeader>
                <CardTitle className="font-['Manrope'] flex items-center gap-2">
                  <Code className="h-5 w-5 text-purple-600" />
                  Embed Chatbot Widget
                </CardTitle>
                <CardDescription>
                  Add the Westgate Mall chatbot to any website
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Direct Link */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-2">Direct Chatbot Link</h4>
                  <div className="flex items-center gap-2">
                    <Input 
                      value={`${window.location.origin}/chat`} 
                      readOnly 
                      className="rounded-none bg-white"
                    />
                    <Button 
                      variant="outline" 
                      className="rounded-none"
                      onClick={() => copyToClipboard(`${window.location.origin}/chat`, 'link')}
                    >
                      {copiedCode === 'link' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                    <Button 
                      variant="outline" 
                      className="rounded-none"
                      onClick={() => window.open(`${window.location.origin}/chat`, '_blank')}
                    >
                      Open
                    </Button>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">Share this link directly with customers</p>
                </div>

                {/* JavaScript Widget */}
                <div>
                  <h4 className="font-semibold mb-2">Website widget (recommended)</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Paste this before the closing <code>&lt;/body&gt;</code> tag on www.westgate.co.ke (or any page). A floating button appears at the bottom right; click to expand the full assistant — chat, movies, seats, snacks, and M-Pesa — inside the panel. Preview at <code>/widget-demo.html</code>.
                  </p>
                  <div className="relative">
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto max-h-64">
                      {generateEmbedCodes(window.location.origin).iframeCode}
                    </pre>
                    <Button 
                      size="sm"
                      className="absolute top-2 right-2 rounded-none"
                      onClick={() => copyToClipboard(generateEmbedCodes(window.location.origin).iframeCode, 'js')}
                    >
                      {copiedCode === 'js' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                {/* React Component */}
                <div>
                  <h4 className="font-semibold mb-2">React Component</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    For React applications
                  </p>
                  <div className="relative">
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto max-h-64">
                      {generateEmbedCodes(window.location.origin).reactCode}
                    </pre>
                    <Button 
                      size="sm"
                      className="absolute top-2 right-2 rounded-none"
                      onClick={() => copyToClipboard(generateEmbedCodes(window.location.origin).reactCode, 'react')}
                    >
                      {copiedCode === 'react' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                {/* Direct iFrame */}
                <div>
                  <h4 className="font-semibold mb-2">Direct iFrame Embed</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Simple iframe for embedding in any page
                  </p>
                  <div className="relative">
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto">
                      {generateEmbedCodes(window.location.origin).directLinkCode}
                    </pre>
                    <Button 
                      size="sm"
                      className="absolute top-2 right-2 rounded-none"
                      onClick={() => copyToClipboard(generateEmbedCodes(window.location.origin).directLinkCode, 'iframe')}
                    >
                      {copiedCode === 'iframe' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
